import { path, sortBy, uniqBy } from 'ramda';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OverrideProperties } from 'type-fest';

import { type ProcessType } from '@frontend/api';
import { type SelectProps, Select } from '@frontend/legacy-components';

import { fetchProcesses } from './data/actions';
import { getHasInitiated, getIsLoading, getProcesses } from './data/reducer';

const sortByName = sortBy((x: ProcessType) => x.name.toLowerCase());

interface Process {
  id: number;
  name: string;
}

interface ProcessOption {
  data: Process;
  label: string;
  value: number;
}

export type ProcessSelectorProps = OverrideProperties<
  SelectProps,
  {
    allowMultiSelect?: boolean;
    onChange?: (selections: number[], selectedOptions: ProcessOption[]) => void;
    selectedValues?: number[];
  }
>;

const ProcessSelector = forwardRef<'div', ProcessSelectorProps>(
  (
    {
      autoFocus = false,
      allowMultiSelect = false,
      defaultMenuIsOpen = false,
      isDisabled = false,
      menuIsOpen,
      menuPortalTarget,
      onBlur,
      onChange,
      onFocus,
      onInputChange,
      selectedValues = [],
      ...restProps
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const hasInitiated = useSelector(getHasInitiated);
    const isLoading = useSelector(getIsLoading);
    const processes = useSelector(getProcesses);

    const fetchProcessesRequest = useCallback(
      () => dispatch(fetchProcesses.request()),
      [dispatch],
    );

    const [searchTerm, setSearchTerm] = useState('');

    let options = sortByName(processes).map((process) => ({
      data: process,
      label: process.name,
      value: process.id,
    }));

    useEffect(() => {
      if (!hasInitiated && !isLoading && selectedValues.length) {
        fetchProcessesRequest();
      }
    }, [fetchProcessesRequest, hasInitiated, isLoading, selectedValues.length]);

    options = uniqBy(path(['value']), options);

    const handleChange = (
      selectionsIn: ProcessOption | ProcessOption[] | null,
    ) => {
      if (!selectionsIn) {
        onChange?.([], []);
        return;
      }

      const selections: ProcessOption[] = !Array.isArray(selectionsIn)
        ? [selectionsIn]
        : selectionsIn;

      // Extract numeric IDs from option objects
      const selectedIds = selections.map((sel) => sel.value);

      // selectedOptions is not needed once we migrate to new Select
      const selectedOptions = options.filter((opt) =>
        selectedIds.includes(opt.value),
      );

      onChange?.(selectedIds, selectedOptions);
    };

    const handleOnFocus = (e: React.FocusEvent<HTMLDivElement>) => {
      onFocus?.(e);

      if (!hasInitiated && !isLoading) {
        fetchProcessesRequest();
      }
    };

    const handleOnInputChange = (value: string) => {
      onInputChange?.(value, { action: 'input-change' });
      setSearchTerm(value);
    };

    // Convert selectedValues (array of IDs) to option objects for Select component
    const selectedOptions = options.filter((opt) =>
      selectedValues.includes(opt.value),
    );

    return (
      <Select
        ref={ref}
        autoFocus={autoFocus}
        closeMenuOnSelect={!allowMultiSelect}
        defaultMenuIsOpen={defaultMenuIsOpen}
        isDisabled={isDisabled}
        inputValue={searchTerm}
        isClearable={!allowMultiSelect}
        isLoading={isLoading}
        isMulti={allowMultiSelect}
        menuPortalTarget={menuPortalTarget}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={handleOnFocus}
        onInputChange={handleOnInputChange}
        options={options}
        menuIsOpen={menuIsOpen}
        placeholder={
          allowMultiSelect ? 'Select one or more processes' : 'Select a process'
        }
        value={allowMultiSelect ? selectedOptions : selectedOptions[0] ?? null}
        {...restProps}
      />
    );
  },
);

ProcessSelector.displayName = 'ProcessSelector';

export default ProcessSelector;
